import { X, FileText, Download, Mail, Phone, ExternalLink, Pencil } from "lucide-react";
import { StatusBadge } from "./StatusBadge";
import {
  DOCUMENTS,
  getContact,
  REQ_STATUS_LABELS,
  REQ_STATUS_TONE,
} from "../data/mockData";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export function RequirementDrawer({ requirement, open, onClose }) {
  const { isAdmin } = useAuth();
  if (!requirement) return null;
  const linkedDocs = DOCUMENTS.filter((d) =>
    requirement.linkedDocumentIds.includes(d.id)
  );
  const contact = getContact(requirement.responsibleContactId);
  const tone = REQ_STATUS_TONE[requirement.status];
  const label = REQ_STATUS_LABELS[requirement.status];

  return (
    <>
      <div
        data-testid="requirement-drawer-backdrop"
        onClick={onClose}
        className={`fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-40 transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        data-testid="requirement-drawer"
        className={`fixed inset-y-0 right-0 w-full max-w-md bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-xl z-50 flex flex-col transform transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-200 dark:border-slate-800">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400">
              {requirement.category}
            </p>
            <h2
              className="font-display text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-50 mt-1"
              data-testid="requirement-drawer-title"
            >
              {requirement.name}
            </h2>
            <div className="mt-2">
              <StatusBadge tone={tone} testId="requirement-drawer-status">
                {label}
              </StatusBadge>
            </div>
          </div>
          <button
            data-testid="requirement-drawer-close"
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">
              Details
            </h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div className="col-span-2">
                <dt className="text-xs text-slate-500 dark:text-slate-400">Authority</dt>
                <dd className="text-slate-900 dark:text-slate-100 mt-0.5">
                  {requirement.authority}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">Reference no.</dt>
                <dd className="text-slate-900 dark:text-slate-100 mt-0.5 font-mono">
                  {requirement.referenceNumber || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">Issue date</dt>
                <dd className="text-slate-900 dark:text-slate-100 mt-0.5">
                  {requirement.issueDate || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">Expiry</dt>
                <dd className="text-slate-900 dark:text-slate-100 mt-0.5">
                  {requirement.expiryDate || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">Category</dt>
                <dd className="text-slate-900 dark:text-slate-100 mt-0.5">
                  {requirement.category}
                </dd>
              </div>
            </dl>
          </section>

          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">
              Notes
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              {requirement.notes}
            </p>
          </section>

          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">
              Linked documents
            </h3>
            {linkedDocs.length === 0 ? (
              <div className="rounded-sm border border-dashed border-slate-300 dark:border-slate-700 p-4 text-sm text-slate-500 dark:text-slate-400">
                No documents on file yet. Information incomplete.
              </div>
            ) : (
              <ul className="space-y-2">
                {linkedDocs.map((d) => (
                  <li
                    key={d.id}
                    data-testid={`drawer-doc-${d.id}`}
                    className="flex items-center justify-between rounded-sm border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 px-3 py-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={14} className="text-slate-500 shrink-0" />
                      <span className="text-sm text-slate-800 dark:text-slate-200 truncate">
                        {d.name}
                      </span>
                    </div>
                    <button
                      data-testid={`drawer-doc-download-${d.id}`}
                      onClick={() => toast.success(`Downloading ${d.name}`)}
                      className="h-7 w-7 flex items-center justify-center rounded-sm text-slate-500 hover:bg-white dark:hover:bg-slate-700"
                      aria-label="Download"
                    >
                      <Download size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {contact && (
            <section>
              <h3 className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">
                Responsible contact
              </h3>
              <div className="rounded-sm border border-slate-200 dark:border-slate-800 p-4">
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  {contact.name}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {contact.role} · {contact.company}
                </p>
                <div className="mt-3 space-y-1.5 text-sm">
                  <a
                    href={`mailto:${contact.email}`}
                    className="flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                  >
                    <Mail size={13} /> {contact.email}
                  </a>
                  <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <Phone size={13} /> {contact.phone}
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-slate-200 dark:border-slate-800 px-6 py-4 flex items-center gap-3">
          {isAdmin ? (
            <>
              <button
                data-testid="requirement-drawer-edit-btn"
                onClick={() => toast("Edit form opened (simulated)")}
                className="flex-1 flex items-center justify-center gap-2 rounded-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60"
              >
                <Pencil size={14} /> Edit requirement
              </button>
              <button
                data-testid="requirement-drawer-open-btn"
                onClick={() => toast("Opened authority record (simulated)")}
                className="flex-1 flex items-center justify-center gap-2 rounded-sm bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white px-3 py-2 text-sm text-white"
              >
                <ExternalLink size={14} /> Open authority record
              </button>
            </>
          ) : (
            <p
              data-testid="requirement-drawer-readonly-note"
              className="text-xs text-slate-500 dark:text-slate-400 w-full text-center"
            >
              Read-only view · Contact your administrator to edit this requirement.
            </p>
          )}
        </div>
      </aside>
    </>
  );
}
